import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, Button, Input, Select, Field, Stat, Textarea } from "@/components/UI";
import { Plus, PencilSimple, Package, CurrencyCircleDollar, Warning, ShoppingCart, MagnifyingGlass, Archive, ArrowCounterClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { UNITS, CATEGORIES, FREQS, EMPTY } from "./constants";
import { PriceUpdateModal } from "./PriceUpdateModal";
import { CountModal } from "./CountModal";
import { QuickPurchaseModal } from "./QuickPurchaseModal";


export function StockItems() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [countModal, setCountModal] = useState(null); // stock item being counted
  const [priceModal, setPriceModal] = useState(null); // stock item whose price is being updated
  const [purchaseModal, setPurchaseModal] = useState(null); // stock item receiving quick purchase
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback((archived = showArchived) => {
    api.get(`/stock?include_archived=${archived ? "true" : "false"}`).then(r => setItems(r.data)).catch(() => {});
    api.get("/stock/summary").then(r => setSummary(r.data)).catch(() => {});
    api.get("/stock/consumption-forecast?days=14").then(r => setForecast(r.data)).catch(() => {});
  }, [showArchived]);
  useEffect(() => { load(); }, [load]);

  // Forecast'ı stock_id -> item map olarak hazırla
  const forecastMap = React.useMemo(() => {
    const m = {};
    (forecast?.items || []).forEach(i => { m[i.id] = i; });
    return m;
  }, [forecast]);

  const submit = async (e) => {
    e.preventDefault();
    const body = {
      ...form,
      quantity: Number(form.quantity),
      unit_price: Number(form.unit_price),
      min_stock: Number(form.min_stock || 0),
      lead_time_days: Number(form.lead_time_days || 3),
    };
    try {
      if (editId) { await api.patch(`/stock/${editId}`, body); toast.success("Güncellendi"); }
      else { await api.post("/stock", body); toast.success("Stok kalemi eklendi"); }
      setShowForm(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };
  const edit = (x) => { setForm({ name: x.name, category: x.category || "yem", unit: x.unit, quantity: x.quantity, unit_price: x.unit_price, count_frequency: x.count_frequency || "aylık", min_stock: x.min_stock ?? 0, lead_time_days: x.lead_time_days ?? 3, supplier: x.supplier || "", notes: x.notes || "" }); setEditId(x.id); setShowForm(true); };
  const archive = async (x) => {
    if (!window.confirm(`'${x.name}' arşive kaldırılsın mı?\nListeden gizlenir ancak Sayım Geçmişi ve finans verileri KORUNUR.`)) return;
    try { await api.patch(`/stock/${x.id}/archive`); toast.success("Arşive kaldırıldı"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Hata"); }
  };
  const unarchive = async (x) => {
    try { await api.patch(`/stock/${x.id}/unarchive`); toast.success("Arşivden geri getirildi"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Hata"); }
  };
  const toggleArchived = () => setShowArchived(v => !v);
  const currentCost = Number(form.quantity || 0) * Number(form.unit_price || 0);

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat testId="stock-total-cost" label="Toplam Stok Maliyeti" value={(summary?.total_cost ?? 0).toLocaleString("tr-TR")} unit="₺" tone="accent" />
        <Stat testId="stock-item-count" label="Kalem Sayısı" value={summary?.item_count ?? 0} unit="adet" />
        <Stat testId="stock-ton" label="Ton Cinsi" value={(summary?.by_unit?.find(x => x.unit === "ton")?.quantity ?? 0).toLocaleString("tr-TR")} unit="ton" tone="ok" />
        <Stat testId="stock-kg" label="Kg Cinsi" value={(summary?.by_unit?.find(x => x.unit === "kg")?.quantity ?? 0).toLocaleString("tr-TR")} unit="kg" />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <button type="button" onClick={toggleArchived} data-testid="toggle-archived-btn"
          className={`text-sm font-medium px-3 py-2 rounded-md border flex items-center gap-1.5 ${showArchived ? "bg-[#1A1D1A] text-white border-[#1A1D1A]" : "bg-white text-[#5e675e] border-[#E5E2DC] hover:border-[#1A1D1A]"}`}>
          <Archive size={15} weight="duotone" /> {showArchived ? "Aktif Kalemleri Göster" : "Arşivi Göster"}
        </button>
        <Button data-testid="add-stock-btn" onClick={() => { setForm(EMPTY); setEditId(null); setShowForm(true); }}>
          <Plus size={16} className="inline mr-1" /> Yeni Stok Kalemi
        </Button>
      </div>

      {showForm && (
        <Card testId="stock-form" className="mb-6">
          <h3 className="heading text-lg mb-4">{editId ? "Stok Kalemini Düzenle" : "Yeni Stok Kalemi Oluştur"}</h3>
          <form onSubmit={submit} className="grid md:grid-cols-3 gap-4">
            <Field label="Kalem Adı *"><Input data-testid="stock-name" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Örn: Kesif Yem, Mısır Silajı" /></Field>
            <Field label="Kategori">
              <Select data-testid="stock-category" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </Select>
            </Field>
            <Field label="Sayım Periyodu *">
              <Select data-testid="stock-freq" value={form.count_frequency} onChange={e => setForm({ ...form, count_frequency: e.target.value })}>
                {FREQS.map(f => <option key={f}>{f}</option>)}
              </Select>
            </Field>
            <Field label="Birim *">
              <Select data-testid="stock-unit" value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })}>
                {UNITS.map(u => <option key={u}>{u}</option>)}
              </Select>
            </Field>
            <Field label="Miktar *"><Input data-testid="stock-quantity" type="number" step="0.01" required value={form.quantity} onChange={e => setForm({ ...form, quantity: e.target.value })} /></Field>
            <Field label="Birim Fiyat (₺) *"><Input data-testid="stock-price" type="number" step="0.01" required value={form.unit_price} onChange={e => setForm({ ...form, unit_price: e.target.value })} /></Field>
            <Field label="Toplam Maliyet (₺)">
              <div className="w-full px-3 py-2.5 border border-[#E5E2DC] rounded-md text-sm bg-[#F7F6F3] font-semibold tabnum" data-testid="stock-live-cost">
                {currentCost.toLocaleString("tr-TR")} ₺
              </div>
            </Field>
            <Field label="Tedarikçi"><Input value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} /></Field>
            <Field label="Minimum Stok (Kritik Eşik)">
              <div className="flex items-center gap-2">
                <Input type="number" min="0" step="0.01" data-testid="stock-min-stock"
                       value={form.min_stock}
                       onChange={e => setForm({ ...form, min_stock: e.target.value })}
                       placeholder="Örn: 200 (kg)" />
                <span className="text-xs text-[#8a938c] whitespace-nowrap">{form.unit}</span>
              </div>
            </Field>
            <Field label="Tedarik Süresi (Gün)">
              <Input type="number" min="0" max="90" step="1" data-testid="stock-lead-time"
                     value={form.lead_time_days}
                     onChange={e => setForm({ ...form, lead_time_days: e.target.value })}
                     placeholder="Sipariş verildikten kaç gün sonra elde? (Vars: 3)" />
            </Field>
            <div className="md:col-span-1">
              <Field label="Notlar"><Textarea rows={1} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
            </div>
            <div className="md:col-span-3 flex gap-2 justify-end">
              <Button type="button" variant="secondary" onClick={() => { setShowForm(false); setForm(EMPTY); setEditId(null); }}>İptal</Button>
              <Button type="submit" data-testid="stock-submit">{editId ? "Kaydet" : "Ekle"}</Button>
            </div>
          </form>
        </Card>
      )}

      {/* KRİTİK STOK & SİPARİŞ UYARISI */}
      {forecast?.alerts && forecast.alerts.length > 0 && (
        <Card className="mb-4 !bg-[#FBECEA] !border-[#E9B5AB]" testId="stock-reorder-card">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-[#F5D5CE] rounded-md">
              <ShoppingCart size={22} weight="duotone" className="text-[#8B2E1D]" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="heading text-base text-[#8B2E1D] flex items-center gap-1.5">
                <Warning size={16} weight="fill" /> Kritik Stok — Sipariş Hatırlatıcısı
              </h4>
              <p className="text-xs text-[#8B2E1D] mt-0.5 mb-2">
                <b>{forecast.alerts.length}</b> yem kalemi kritik seviyede. Tüketim hızına göre tedarik süresi + 2 gün emniyet payının altında.
              </p>
              <div className="space-y-1.5" data-testid="stock-reorder-list">
                {forecast.alerts.map(a => (
                  <div key={a.id} className="bg-white border border-[#E9B5AB] px-3 py-2 rounded flex items-center justify-between gap-2 text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <b className="text-[#8B2E1D]">{a.name}</b>
                      <span className="text-xs text-[#5e675e]">·</span>
                      <span className="text-xs tabnum">
                        <b>{a.quantity_kg != null ? `${a.quantity_kg} kg` : `${a.quantity} ${a.unit}`}</b> kaldı
                      </span>
                      {a.daily_avg_kg > 0 && (
                        <>
                          <span className="text-xs text-[#5e675e]">·</span>
                          <span className="text-xs tabnum text-[#8B6914]">{a.daily_avg_kg} kg/gün</span>
                        </>
                      )}
                      {a.days_remaining != null && (
                        <span className="text-xs bg-[#C45B45] text-white px-1.5 py-0.5 rounded font-semibold">
                          {a.days_remaining} gün
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-[#8a938c] hidden md:block truncate max-w-md">{a.reorder_reason}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card className="!p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="data-table" data-testid="stock-table">
            <thead>
              <tr>
                <th>Kalem</th>
                <th>Kategori</th>
                <th>Periyot</th>
                <th className="text-right">Mevcut Miktar</th>
                <th className="text-right">Günlük Tüketim</th>
                <th className="text-right">Kalan Gün</th>
                <th className="text-right whitespace-nowrap">Önerilen Sipariş</th>
                <th className="text-right">Birim Fiyat</th>
                <th className="text-right">Maliyet</th>
                <th>Son Sayım</th>
                <th className="text-right whitespace-nowrap">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {items.map(x => {
                const fc = forecastMap[x.id];
                return (
                <tr key={x.id} data-testid={`stock-row-${x.name.replace(/\s/g, '-')}`}
                    className={x.archived ? "opacity-60" : (fc?.reorder_alert ? "bg-[#FBECEA]/40" : "")}>
                  <td className="font-semibold">
                    <Package size={14} weight="duotone" className="inline mr-1.5 text-[#417251]" />{x.name}
                    {x.archived && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-[#8a938c] text-white px-1.5 py-0.5 rounded" data-testid={`archived-badge-${x.name.replace(/\s/g, '-')}`}>Arşivde</span>}
                    {fc?.reorder_alert && (
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#C45B45] text-white px-1.5 py-0.5 rounded animate-pulse"
                            data-testid={`stock-reorder-badge-${x.name.replace(/\s/g, '-')}`}>
                        <ShoppingCart size={10} weight="fill" /> Sipariş Ver
                      </span>
                    )}
                    {x.min_stock > 0 && (
                      <div className="text-[10px] text-[#8a938c] mt-0.5">min: {Number(x.min_stock).toLocaleString("tr-TR")} {x.unit} · tedarik: {x.lead_time_days || 3}g</div>
                    )}
                  </td>
                  <td className="text-[#5e675e]">{x.category || "—"}</td>
                  <td>
                    <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{ background: x.count_frequency === "haftalık" ? "#FBE9E4" : "#E4EEE6", color: x.count_frequency === "haftalık" ? "#C45B45" : "#2D4A36" }}>
                      {x.count_frequency || "aylık"}
                    </span>
                  </td>
                  <td className="text-right tabnum">
                    <b>{Number(x.quantity).toLocaleString("tr-TR")}</b> {x.unit}
                    {fc?.quantity_kg != null && x.unit !== "kg" && (
                      <div className="text-[10px] text-[#8a938c]">= {Number(fc.quantity_kg).toLocaleString("tr-TR")} kg</div>
                    )}
                  </td>
                  <td className="text-right tabnum">
                    {fc?.daily_avg_kg > 0
                      ? <span className="text-[#8B6914] font-semibold">{Number(fc.daily_avg_kg).toLocaleString("tr-TR")} <span className="text-[10px] text-[#8a938c] font-normal">kg/gün</span></span>
                      : <span className="text-xs text-[#8a938c]">—</span>}
                  </td>
                  <td className="text-right tabnum">
                    {fc?.days_remaining != null
                      ? <span className={`font-semibold ${fc.reorder_alert ? "text-[#C45B45]" : fc.days_remaining < 30 ? "text-[#8B6914]" : "text-[#2D4A36]"}`}
                              data-testid={`stock-days-remaining-${x.name.replace(/\s/g, '-')}`}>
                          {fc.days_remaining} gün
                          {fc.predicted_empty_date && <div className="text-[10px] text-[#8a938c] font-normal">→ {fc.predicted_empty_date}</div>}
                        </span>
                      : <span className="text-xs text-[#8a938c]">—</span>}
                  </td>
                  <td className="text-right tabnum whitespace-nowrap">
                    {fc?.reorder_date
                      ? <span className={`font-semibold ${fc.reorder_alert ? "text-[#C45B45]" : "text-[#5e675e]"}`}
                              data-testid={`stock-reorder-date-${x.name.replace(/\s/g, '-')}`}>
                          {fc.reorder_date}
                        </span>
                      : <span className="text-xs text-[#8a938c]">—</span>}
                  </td>
                  <td className="text-right tabnum">{Number(x.unit_price).toLocaleString("tr-TR")} ₺</td>
                  <td className="text-right tabnum font-semibold text-[#2D4A36]">{Number(x.total_cost).toLocaleString("tr-TR")} ₺</td>
                  <td>
                    {x.last_count_date ? (
                      <div className="text-sm">
                        <div className="text-[#5e675e] tabnum">{x.last_count_date}</div>
                        {x.reminder_status === "gecikti" && (
                          <span className="text-xs inline-block px-1.5 py-0.5 rounded-full bg-[#C45B45] text-white mt-0.5" data-testid={`reminder-badge-${x.name.replace(/\s/g, '-')}`}>
                            {Math.abs(x.days_until_due)} gün gecikmiş
                          </span>
                        )}
                        {x.reminder_status === "yaklaştı" && (
                          <span className="text-xs inline-block px-1.5 py-0.5 rounded-full bg-[#DDA74F] text-white mt-0.5" data-testid={`reminder-badge-${x.name.replace(/\s/g, '-')}`}>
                            {x.days_until_due} gün kaldı
                          </span>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs inline-block px-1.5 py-0.5 rounded-full bg-[#8a938c] text-white" data-testid={`reminder-badge-${x.name.replace(/\s/g, '-')}`}>
                        Hiç sayım yok
                      </span>
                    )}
                  </td>
                  <td className="text-right whitespace-nowrap">
                    {x.archived ? (
                      <button onClick={() => unarchive(x)} title="Arşivden geri getir"
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-[#E4EEE6] text-[#2D4A36] text-xs font-semibold hover:bg-[#d4e3d8] border border-[#c2d6c8]"
                        data-testid={`unarchive-btn-${x.name.replace(/\s/g, '-')}`}>
                        <ArrowCounterClockwise size={14} weight="bold" /> Geri Getir
                      </button>
                    ) : (
                      <div className="inline-flex items-center gap-0.5">
                        <button onClick={() => setCountModal(x)} title="Sayım (Mutabakat)" className="p-1.5 rounded-md text-[#1A1D1A] hover:bg-[#EAF1EC]" data-testid={`count-btn-${x.name.replace(/\s/g, '-')}`}><MagnifyingGlass size={17} weight="bold" /></button>
                        <button onClick={() => setPurchaseModal(x)} title="Hızlı Alım (yeni mal girişi)" className="p-1.5 rounded-md text-white bg-[#2D6A4F] hover:bg-[#245A42]" data-testid={`purchase-btn-${x.name.replace(/\s/g, '-')}`}><Plus size={16} weight="bold" /></button>
                        <button onClick={() => setPriceModal(x)} title="Fiyat Güncelle" className="p-1.5 hover:bg-[#FEF6E0] text-[#8B6914] rounded" data-testid={`price-btn-${x.name.replace(/\s/g, '-')}`}><CurrencyCircleDollar size={16} weight="duotone" /></button>
                        <button onClick={() => edit(x)} title="Kalemi düzenle" className="p-1.5 hover:bg-[#F0EEE8] rounded" data-testid={`edit-stock-${x.name.replace(/\s/g, '-')}`}><PencilSimple size={16} /></button>
                        <button onClick={() => archive(x)} title="Arşive Kaldır" className="p-1.5 hover:bg-[#FEF3E2] text-[#8B6914] rounded" data-testid={`archive-btn-${x.name.replace(/\s/g, '-')}`}><Archive size={16} weight="duotone" /></button>
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
              {!items.length && <tr><td colSpan={11} className="text-center py-10 text-[#8a938c]">Henüz stok kalemi yok.</td></tr>}
            </tbody>
            {items.length > 0 && (
              <tfoot>
                <tr className="bg-[#F7F6F3] font-semibold">
                  <td colSpan={8} className="text-right py-3">Toplam Stok Maliyeti:</td>
                  <td className="text-right py-3 tabnum text-[#2D4A36]" data-testid="stock-footer-total">{(summary?.total_cost ?? 0).toLocaleString("tr-TR")} ₺</td>
                  <td colSpan={2}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>

      {countModal && <CountModal item={countModal} onClose={() => setCountModal(null)} onDone={() => { setCountModal(null); load(); }} />}
      {purchaseModal && <QuickPurchaseModal item={purchaseModal} onClose={() => setPurchaseModal(null)} onDone={() => { setPurchaseModal(null); load(); }} />}
      {priceModal && <PriceUpdateModal item={priceModal} onClose={() => setPriceModal(null)} onDone={() => { setPriceModal(null); load(); }} />}
    </div>
  );
}
